import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@comps/ui/resizable';
import { FetchItemsFn, DriveItem } from '@dto/drive';
import { EditRightAccess, MembersRightType, hasGlobalRightAccess } from '@dto/Users';
import { listDriveItems } from '@utils/driveApi';
import { useCallback, useRef, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useModelStore, useUserStore } from 'src/store';
import Members from '../members/Members';
import Roles from '../members/Roles';
import DrivePanel from './DrivePanel';
import DriveSidebar from './DriveSidebar';

interface DriveWorkspaceLayoutProps {
    wsKey: string;
}

function DriveWorkspaceLayout({ wsKey }: DriveWorkspaceLayoutProps) {
    const [currentPath, setCurrentPath] = useState('');
    const cacheRef = useRef<Map<string, DriveItem[]>>(new Map());
    const navigate = useNavigate();

    const navigateToDrive = useCallback((path: string) => {
        setCurrentPath(path);
        navigate(`/workspaces/${wsKey}`);
    }, [wsKey, navigate]);

    const fetchItems: FetchItemsFn = useCallback(async (path: string): Promise<DriveItem[]> => {
        if (cacheRef.current.has(path)) {
            return cacheRef.current.get(path)!;
        }
        const res = await listDriveItems(wsKey, path);
        const items = (res && 'items' in res) ? res.items : [];
        cacheRef.current.set(path, items);
        return items;
    }, [wsKey]);

    const user = useUserStore(state => state.user);
    const userRole = useModelStore(state => state.userRole);
    const hasMembersRight = hasGlobalRightAccess(user, userRole, MembersRightType, EditRightAccess);

    return (
        <ResizablePanelGroup direction="horizontal" className="w-full h-full">
            <ResizablePanel defaultSize={20} minSize={12} maxSize={40}>
                <DriveSidebar
                    currentPath={currentPath}
                    onNavigate={navigateToDrive}
                    fetchItems={fetchItems}
                    hasMembersRight={hasMembersRight}
                />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={80}>
                <Routes>
                    <Route path="/members" element={<Members />} />
                    <Route path="/roles" element={<Roles />} />
                    <Route path="*" element={
                        <DrivePanel
                            wsKey={wsKey}
                            currentPath={currentPath}
                            onNavigate={setCurrentPath}
                            fetchItems={fetchItems}
                        />
                    } />
                </Routes>
            </ResizablePanel>
        </ResizablePanelGroup>
    );
}

export default DriveWorkspaceLayout;
